import axios from "api/axios";

export interface BulkAssignError {
  employee_id: number;
  detail: string;
}

export interface BulkAssignResult {
  assigned: unknown[];
  errors: BulkAssignError[];
}

export type AssignmentStatus =
  | "new"
  | "reviewing"
  | "applied"
  | "interviewing"
  | "offer_received"
  | "hired"
  | "rejected"
  | "not_relevant"
  | "archived";

export interface TheirStackJob {
  id: number;
  idempotency_key: string;
  source_job_id: string;
  job_title: string;
  company_name: string;
  company_domain?: string | null;
  job_url: string;
  original_url?: string | null;
  description?: string | null;
  remote: boolean;
  hybrid: boolean;
  location?: string | null;
  location_text?: string | null;
  country?: string | null;
  country_code?: string | null;
  date_posted?: string | null;
  discovered_at?: string | null;
  seniority?: string | null;
  employment_type?: string | null;
  technology_slugs?: string | null;
  keyword_slugs?: string | null;
  stack_source?: string | null;
  remote_scope?: string | null;
  llm_analysis_metadata?: string | null;
  company_can_apply?: boolean | null;
  company_analysis_metadata?: string | null;
  raw_job?: string | null;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface TheirStackAssignment {
  // TheirStackJob fields (slim — no description/raw_job in list)
  id: number;
  idempotency_key: string;
  source_job_id: string;
  job_title: string;
  company_name: string;
  company_domain?: string | null;
  job_url: string;
  original_url?: string | null;
  description?: string | null;
  remote: boolean;
  hybrid: boolean;
  location?: string | null;
  location_text?: string | null;
  country?: string | null;
  country_code?: string | null;
  date_posted?: string | null;
  discovered_at?: string | null;
  seniority?: string | null;
  employment_type?: string | null;
  technology_slugs?: string | null;
  keyword_slugs?: string | null;
  stack_source?: string | null;
  remote_scope?: string | null;
  llm_analysis_metadata?: string | null;
  company_can_apply?: boolean | null;
  company_analysis_metadata?: string | null;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;

  // Assignment overlay
  assignment_id: number;
  assigned_by: "auto" | "admin";
  status: AssignmentStatus;
  assignment_notes?: string | null;
  assignment_from_status?: string | null;
  assignment_created_at?: string | null;
  assignment_updated_at?: string | null;
  has_enhancement: boolean;
  has_enhancement_logs: boolean;
  enhanced_at?: string | null;
  cv_id_enhanced?: number | null;
  employee_id?: number; // admin view only
  enhanced_cv_data?: any | null; // detail only

  has_cover_letter_enhancement: boolean;
  has_cover_letter_enhancement_logs: boolean;
  cover_letter_enhanced_at?: string | null;
  cover_letter_id_enhanced?: number | null;
  enhanced_cover_letter_data?: any | null; // detail only
}

export interface TheirStackTechManager {
  id: number;
  employee_code: string;
  name: string;
  email: string;
  tech_tags: string[];
}

export interface PagedResult<T> {
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: T[];
}

export interface AssignmentHistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  notes: string | null;
  changed_at: string | null;
}

export interface JobFilters {
  page?: number;
  page_size?: number;
  seniority?: string;
  employment_type?: string;
  search?: string;
  active_only?: boolean;
  unassigned_only?: boolean;
  company_can_apply_only?: boolean;
}

export interface AssignmentFilters {
  page?: number;
  page_size?: number;
  employee_id?: number;
  status_filter?: string;
  hide_archived?: boolean;
  seniority?: string;
  employment_type?: string;
  search?: string;
  company_can_apply_only?: boolean;
}

export const STATUS_OPTIONS: { value: AssignmentStatus; label: string }[] = [
  { value: "new",            label: "New" },
  { value: "reviewing",      label: "Reviewing" },
  { value: "applied",        label: "Applied" },
  { value: "interviewing",   label: "Interviewing" },
  { value: "offer_received", label: "Offer Received" },
  { value: "hired",          label: "Hired" },
  { value: "rejected",       label: "Rejected" },
  { value: "not_relevant",   label: "Not Relevant" },
  { value: "archived",       label: "Archived" },
];

const ADMIN = "/admin/theirstack-jobs";
const EMP   = "/employee/theirstack-jobs";
const base  = (isAdmin: boolean) => (isAdmin ? ADMIN : EMP);

function clean(filters: object) {
  return Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""));
}

export async function fetchTheirStackJobs(filters: JobFilters = {}): Promise<PagedResult<TheirStackJob>> {
  const res = await axios.get(ADMIN, { params: clean(filters) });
  return res.data;
}

export async function activateJob(id: number): Promise<TheirStackJob> {
  const res = await axios.patch(`${ADMIN}/${id}/activate`);
  return res.data;
}

export async function deactivateJob(id: number): Promise<TheirStackJob> {
  const res = await axios.patch(`${ADMIN}/${id}/deactivate`);
  return res.data;
}

export async function assignJob(jobId: number, employeeIds: number[]): Promise<BulkAssignResult> {
  const res = await axios.post(`${ADMIN}/${jobId}/assign`, { employee_ids: employeeIds });
  return res.data;
}

export async function unassignJob(jobId: number, employeeId: number): Promise<void> {
  await axios.delete(`${ADMIN}/${jobId}/assign/${employeeId}`);
}

export interface ReassignedAssignment {
  id: number;
  employee_id: number;
  assigned_by: "auto" | "admin";
}

export async function reassignAssignment(assignmentId: number, employeeId: number): Promise<ReassignedAssignment> {
  const res = await axios.patch(`${ADMIN}/assignments/${assignmentId}/reassign`, { employee_id: employeeId });
  return res.data;
}

export interface JobAssignee {
  employee_id: number;
  employee_code: string;
  name: string;
  status: AssignmentStatus;
  assigned_by: "auto" | "admin";
}

export async function fetchJobAssignees(jobId: number): Promise<JobAssignee[]> {
  const res = await axios.get(`${ADMIN}/${jobId}/assignees`);
  return res.data;
}

export async function fetchAssignmentStatusCounts(
  isAdmin: boolean,
  employeeId?: number,
): Promise<Record<string, number>> {
  const params = isAdmin && employeeId ? { employee_id: employeeId } : {};
  const res = await axios.get(`${base(isAdmin)}/assignments/status-counts`, { params });
  return res.data;
}

export async function fetchAssignments(
  filters: AssignmentFilters = {},
  isAdmin = false,
): Promise<PagedResult<TheirStackAssignment>> {
  const res = await axios.get(`${base(isAdmin)}/assignments`, { params: clean(filters) });
  return res.data;
}

export async function fetchAssignmentDetail(
  assignmentId: number,
  isAdmin: boolean,
): Promise<TheirStackAssignment> {
  const res = await axios.get(`${base(isAdmin)}/assignments/${assignmentId}`);
  return res.data;
}

export async function fetchAssignmentHistory(
  assignmentId: number,
  isAdmin: boolean,
): Promise<AssignmentHistoryEntry[]> {
  const res = await axios.get(`${base(isAdmin)}/assignments/${assignmentId}/history`);
  return res.data;
}

export async function fetchEnhancementLogs(
  assignmentId: number,
  isAdmin: boolean,
): Promise<any[]> {
  const res = await axios.get(`${base(isAdmin)}/assignments/${assignmentId}/enhancement-logs`);
  return res.data.logs ?? [];
}

export async function fetchCoverLetterEnhancementLogs(
  assignmentId: number,
  isAdmin: boolean,
): Promise<any[]> {
  const res = await axios.get(`${base(isAdmin)}/assignments/${assignmentId}/cover-letter-enhancement-logs`);
  return res.data.logs ?? [];
}

export async function updateAssignmentStatus(
  assignmentId: number,
  status: AssignmentStatus,
  notes: string | null,
  isAdmin: boolean,
): Promise<void> {
  await axios.patch(`${base(isAdmin)}/assignments/${assignmentId}/status`, { status, notes });
}

export async function enhanceCvForAssignment(
  assignmentId: number,
  cvId: number,
  isAdmin: boolean,
  employeeCode?: string,
): Promise<{ assignment_id: number; cv_id: number; enhanced_at: string; enhanced_cv_data: any; has_enhancement_logs: boolean }> {
  const url = `${base(isAdmin)}/assignments/${assignmentId}/enhance-cv`;
  const body = isAdmin ? { cv_id: cvId, employee_code: employeeCode } : { cv_id: cvId };
  const res = await axios.post(url, body);
  return res.data;
}

export async function enhanceCoverLetterForAssignment(
  assignmentId: number,
  coverLetterId: number,
  isAdmin: boolean,
  employeeCode?: string,
  /** The CV currently selected in the sibling CvEnhancementSection's picker — passed
   * explicitly so grounding doesn't depend on the (much slower) CV enhancement pipeline
   * having already written its result back to the assignment when both run concurrently
   * via "Enhance All". Falls back server-side to the assignment's already-linked CV when
   * omitted. */
  cvId?: number | null,
): Promise<{ assignment_id: number; cover_letter_id: number; enhanced_at: string; enhanced_cover_letter_data: any; has_cover_letter_enhancement_logs: boolean }> {
  const url = `${base(isAdmin)}/assignments/${assignmentId}/enhance-cover-letter`;
  const body = isAdmin
    ? { cover_letter_id: coverLetterId, employee_code: employeeCode, cv_id: cvId ?? null }
    : { cover_letter_id: coverLetterId, cv_id: cvId ?? null };
  const res = await axios.post(url, body);
  return res.data;
}

export async function fetchTechManagers(): Promise<TheirStackTechManager[]> {
  const res = await axios.get(`${ADMIN}/tech-managers`);
  return res.data;
}

export async function updateTechTags(
  employeeCode: string,
  tags: string[],
): Promise<{ employee_code: string; tech_tags: string[] }> {
  const res = await axios.patch(
    `${ADMIN}/tech-managers/${encodeURIComponent(employeeCode)}/tech-tags`,
    { tags },
  );
  return res.data;
}

export async function fetchEmployeeCvsForAdmin(
  employeeCode: string,
): Promise<{ id: number; title: string; category_id?: number | null }[]> {
  const res = await axios.get(`${ADMIN}/employee-cvs/${encodeURIComponent(employeeCode)}`);
  return res.data;
}

export async function fetchEmployeeCoverLettersForAdmin(
  employeeCode: string,
): Promise<{ id: number; title: string; category_id?: number | null }[]> {
  const res = await axios.get(`${ADMIN}/employee-cover-letters/${encodeURIComponent(employeeCode)}`);
  return res.data;
}
